// About.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import "../Style/about.css";

import heroImg from "../Images/About/about1.png";
import visionImg from "../Images/About/about2.png";
import missionImg from "../Images/About/about3.png";
import produitImg from "../Images/About/about4.png";
import contactImg from "../Images/About/about5.png";

const NS = "about";

const ILLUSTRATIONS = {
  hero: heroImg,
  vision: visionImg,
  mission : missionImg,
  product: produitImg,
  contact: contactImg,
};

function Illustration({ src, alt, variant = "default" }) {
  return (
    <div className={`about-illustrationFrame about-illustrationFrame--${variant}`}>
      {src ? (
        <img
          className="about-illustrationImg"
          src={src}
          alt={alt || "Illustration"}
          loading={variant === "hero" ? "eager" : "lazy"}
        />
      ) : (
        <div className="about-illustration" role="img" aria-label={alt || "Illustration placeholder"} />
      )}
    </div>
  );
}

function Section({ id, title, paragraphs = [], illustrationSuggestion, illustrationSrc }) {
  return (
    <section id={id} className="about-section" aria-labelledby={`${id}-title`}>
      <div className="about-container">
        <div className="about-split">
          <div className="about-copy">
            <h2 className="about-h2" id={`${id}-title`}>
              {title}
            </h2>

            <div className="about-prose">
              {Array.isArray(paragraphs) &&
                paragraphs.map((p, i) => (
                  <p key={`${id}-p-${i}`} className="about-p">
                    {p}
                  </p>
                ))}
            </div>
          </div>

          <div className="about-figure" aria-label={`${title} illustration`}>
            <Illustration
              src={illustrationSrc}
              alt={illustrationSuggestion || `${title} illustration`}
              variant="default"
            />

            {illustrationSuggestion ? (
              <p className="about-figure__caption">
                <em>{illustrationSuggestion}</em>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  const { t } = useTranslation(NS);

  // Certains setups i18n renvoient n'importe quoi sur t("")
  // => on garde page en "best effort" mais on lit chaque bloc explicitement si besoin
  const page = (() => {
    try {
      const res = t("", { returnObjects: true });
      return typeof res === "object" && res !== null ? res : {};
    } catch {
      return {};
    }
  })();

  const hero = page?.hero || t("hero", { returnObjects: true });
  const sections = page?.sections || t("sections", { returnObjects: true });
  const ctas = page?.ctas || t("ctas", { returnObjects: true });

  // ✅ FIX ICI : on lit contact explicitement
  const contact = page?.contact || t("contact", { returnObjects: true });

  const safeSections = Array.isArray(sections) ? sections : [];

  return (
    <main className="about-page" aria-label={page?.seo?.ariaLabel || "About Life"}>
      {/* HERO */}
      <header className="about-hero">
        <div className="about-container">
          <div className="about-hero__grid">
            <div className="about-hero__copy">
              <h1 className="about-h1">{hero?.title}</h1>

              <div className="about-prose about-prose--hero" aria-label="Hero subtitle">
                {Array.isArray(hero?.subtitle) &&
                  hero.subtitle.map((p, i) => (
                    <p key={`hero-sub-${i}`} className="about-p about-p--hero">
                      {p}
                    </p>
                  ))}
              </div>

              <div className="about-ctaRow">
                <a className="about-btn about-btn--primary" href={ctas?.discover?.href || "/"}>
                  {ctas?.discover?.label || "Discover Life"}
                </a>
              </div>
            </div>

            <aside className="about-hero__figure" aria-label="Hero illustration">
              <Illustration
                src={ILLUSTRATIONS.hero}
                alt={hero?.illustrationSuggestion || "Hero illustration"}
                variant="hero"
              />

              {hero?.illustrationSuggestion ? (
                <p className="about-figure__caption">
                  <em>{hero.illustrationSuggestion}</em>
                </p>
              ) : null}
            </aside>
          </div>
        </div>
      </header>

      {/* SECTIONS */}
      {safeSections.map((s) => (
        <Section
          key={s.id}
          id={s.id}
          title={s.title}
          paragraphs={s.paragraphs}
          illustrationSuggestion={s.illustrationSuggestion}
          illustrationSrc={ILLUSTRATIONS[s.id]}
        />
      ))}

      {/* CONTACT / BUILD WITH US */}
      <section
        id="contact"
        className="about-section about-section--contact"
        aria-labelledby="contact-title"
      >
        <div className="about-container">
          <div className="about-contact">
            <h2 className="about-h2" id="contact-title">
              {contact?.title || "Contact / Build with us"}
            </h2>

            <div className="about-prose">
              {Array.isArray(contact?.paragraphs) &&
                contact.paragraphs.map((p, i) => (
                  <p key={`contact-p-${i}`} className="about-p">
                    {p}
                  </p>
                ))}
            </div>

            <div className="about-contact__cta">
              <a className="about-btn about-btn--ghost" href={ctas?.contact?.href || "/contact"}>
                {ctas?.contact?.label || "Contact us"}
              </a>
            </div>

            <div className="about-contact__figure" aria-label="Build with us illustration">
              <Illustration
                src={ILLUSTRATIONS.contact}
                alt={contact?.illustrationSuggestion || "Contact illustration"}
                variant="default"
              />

              {contact?.illustrationSuggestion ? (
                <p className="about-figure__caption">
                  <em>{contact.illustrationSuggestion}</em>
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
